import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

export interface VariantReorderInfo {
  variant_id: string;
  product_id: string;
  company_id: string;
  product_name: string;
  sku: string;
  size: string | null;
  color: string | null;
  on_hand: number;
  units_30d: number;
  units_per_day: number;
  last_sale_at: string | null;
  lead_time_days: number;
  system_threshold: number;
  system_recommended_qty: number;
  override_threshold: number | null;
  effective_threshold: number;
  is_low_stock: boolean;
}

const STALE = 30 * 1000;

export function useVariantReorderInfo(variantId: string | undefined) {
  return useQuery({
    queryKey: ['inventory', 'variant-reorder', variantId],
    enabled: !!variantId,
    staleTime: STALE,
    queryFn: async (): Promise<VariantReorderInfo | null> => {
      // RPC name not yet in generated types — regen yarn supabase:gen:types after migration deploy.
      // @ts-expect-error pending types regen for get_variant_reorder_info (#742)
      const { data, error } = await supabase.rpc('get_variant_reorder_info', {
        p_variant_id: variantId!,
      });
      if (error) throw error;
      const rows = ((data ?? []) as unknown) as VariantReorderInfo[];
      return rows[0] ?? null;
    },
  });
}

export function useUpdateVariantThreshold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { variantId: string; threshold: number | null }) => {
      const { error } = await supabase
        .from('product_variants')
        .update({ reorder_threshold_override: input.threshold })
        .eq('id', input.variantId);
      if (error) throw error;
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'variant-reorder', input.variantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'low-stock'] });
    },
  });
}
